
import { map,  tap } from 'rxjs/operators'
import { PlannedPost, PendingPost } from 'interfaces'
import StoreState, { getState, StoreThunkAction, StoreThunkDispatch, createAppAsyncThunk } from 'store/state'
import { Observable, firstValueFrom } from 'rxjs'
import { V1 } from './net'
import { postsResponseAdapter, autoPostSlotAdapter, postAdapter } from '../adapters/posts'
import { AutoPostSlot } from '../state'
import { mapPostsToBuckets } from './mapPostsToBuckets'
import { createAction } from '@reduxjs/toolkit'
import { currentUserSelector } from 'services/users/selectors'
import { ContentType } from 'shared'

export const getPostSlotsSuccess = createAction<{ slots: AutoPostSlot[], page: number }>('posts/planned/getSlotsSuccess')

/** FETCH Planned */
const PAGE_SIZE_DEFAULT = 50
export type PlannedPostFetchResponseData = {
  posts: PlannedPost[],
  totalPostsCount: number,
  hasPendingPosts: boolean,
  page: number
}

export function fetchPlannedPosts(
  page: number,
  pageIds: string[],
  query?: string,
  types?: ContentType[],
  withEmptySlots?: boolean,
  filter?: 'scheduled' | 'queued',
  searchInSummary?: boolean,
  withVirtualPosts?: boolean
): StoreThunkAction<Observable<PlannedPostFetchResponseData>> {
  return (dispatch: StoreThunkDispatch, getState: getState) => {
    const isBeta = currentUserSelector(getState() as StoreState)?.roles?.isBeta
    return dispatch(
      V1.getPlannedPosts(page, pageIds, PAGE_SIZE_DEFAULT, query, types, withEmptySlots, filter, searchInSummary, withVirtualPosts, isBeta)
    )
      .pipe(tap((response) => {
        const slots = response.posts.filter(p => p.autoPost && !p.isVirtual && p.id !== 'empty' && p.time).map(autoPostSlotAdapter)
        dispatch(getPostSlotsSuccess({ slots, page }))
      })).pipe(
        map(response => {
          const posts: PlannedPost[] = postsResponseAdapter<PlannedPost>(response)
          return {
            posts: dispatch(mapPostsToBuckets(posts)),
            totalPostsCount: response.postsCount,
            hasPendingPosts: response.hasPendingPosts,
            page
          }
        }))
  }
}

type PendingResponse = { posts: PendingPost[], page: number, totalPostsCount: number }
/** FETCH Pending */
export function fetchPendingPosts(
  page: number, pageIds: string[], query?: string, types?: ContentType[], searchInSummary?: boolean
): StoreThunkAction<Observable<PendingResponse>> {
  return (dispatch: StoreThunkDispatch, getState: getState) => {
    const isBeta = currentUserSelector(getState() as StoreState)?.roles?.isBeta
    return dispatch(V1.getPendingPosts(page, pageIds, query, types, searchInSummary, isBeta)).pipe(
      map(response => {
        const posts = postsResponseAdapter<PendingPost>(response)
        return ({
          posts: dispatch(mapPostsToBuckets(posts)),
          page,
          totalPostsCount: response.postsCount
        })
      }))
  }
}

export function fetchPlannedPostsForRange(
  ppids: string[],
  startDate: string,
  endDate: string,
  withEmptySlots?: boolean,
  withVirtualPosts?: boolean
): StoreThunkAction<Observable<{ posts: PlannedPost[], totalPostsCount: number }>> {
  return (dispatch: StoreThunkDispatch) => {
    return dispatch(V1.getPlannedPostsForRange(startDate, endDate, ppids, withEmptySlots, withVirtualPosts)).pipe(
      map(response => {
        const posts = postsResponseAdapter<PlannedPost>(response)
        return {
          posts: dispatch(mapPostsToBuckets(posts)),
          slots: response.posts.filter(p => p.autoPost && !p.isVirtual && p.id !== 'empty' && p.time).map(autoPostSlotAdapter),
          totalPostsCount: response.postsCount,
          hasPendingPosts: response.hasPendingPosts
        }
      }))
  }
}

export const getPostById = createAppAsyncThunk(
  'posts/planned/getById',
  async (postId: string, { dispatch }) => {
    const response = await firstValueFrom(dispatch(V1.getPostById(postId)))
    const post = postAdapter<PlannedPost>(response, 0)
    const [postWithBucket] = dispatch(mapPostsToBuckets([post]))
    return postWithBucket
  }
)
